import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent
} from '@dnd-kit/core';
import { arraySwap, SortableContext, rectSwappingStrategy } from '@dnd-kit/sortable';
import Grid from './components/Grid';
import SortableItem from './components/SortableItem';
import Item from './components/Item';
import VictoryModal from "./components/VictoryModal";
import { useLocalStorage } from "./utils/useLocalStorage";
import { SUBMIT_USER_GAME, START_USER_GAME, RECORD_USER_GAME_HINT } from "./graphql/Mutations";
import HelpModal from "./components/HelpModal";
import GreetingModal from "./components/GreetingModal";
import NavigationBar from "./components/NavigationBar";
import useImage from "./utils/useImage";
import { Button } from "react-bootstrap";
import { trackEvent } from "./utils/analytics";
import { formatTime, getGameNumber, getSolutionsFontColor, getTodaysDate, getUserId, solutionValid } from "./utils/gameUtils";
import { getTileDisplayWidth } from "./utils/tileLayout";
import { GameOutput } from "./graphql-codegen/types";
import ErrorModal from "./components/ErrorModal";
import confetti from 'canvas-confetti';
import OfferModal from "./components/OfferModal";
import { generateTileSVG } from "./utils/tileGenerator";
import { GET_GAME_OF_THE_DAY, GET_USER_GAME_PERCENTILE } from "./graphql/Queries";

export default function GameOfTheDay() {
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const [gameTiles, setGameTiles] = useState<string[]>([]);
  const [solutionTiles, setSolutionTiles] = useState<string[]>([]);
  const [activeId, setActiveId] = useState(null);
  const [activeGameDate, setActiveGameDate] = useState<string>(null);
  const [gameExists, setGameExists] = useState<boolean>(true);
  const [modalState, setModalState] = useState<string>('greeting');
  const [forcedFirstHelp, setForcedFirstHelp] = useState(false);
  const goToAdminViaShortcut = useCallback(() => {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    window.location.assign(base ? `${base}/admin` : '/admin');
  }, []);
  const offerClaimRef = useRef(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastHintUsed, setLastHintUsed] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [movesPlayed, setMovesPlayed] = useState(0);
  const [numSolutions, setNumSolutions] = useState(0);
  const [mode, setMode] = useState<string>(null);
  const [gameData, setGameData] = useState<GameOutput>(null);
  const [timePercentile, setTimePercentile] = useState<number | null>(null);
  const [showTimePercentile, setShowTimePercentile] = useState(false);
  const [victoryStats, setVictoryStats] = useState<{ streak: number; maxStreak: number; wins: number } | null>(null);

  const { getItem: getStoredUserId, setItem: setStoredUserId } = useLocalStorage('userId');

  const { getItem: getStoredLatestClassicDate, setItem: setStoredLatestClassicDate } = useLocalStorage('latestClassicDate');
  const { getItem: getStoredClassicTileOrder, setItem: setStoredClassicTileOrder} = useLocalStorage('classicTileOrder');
  const { getItem: getStoredClassicCompleted, setItem: setStoredClassicCompleted } = useLocalStorage('classicCompleted');
  const { getItem: getStoredClassicElapsedTime, setItem: setStoredClassicElapsedTime } = useLocalStorage('elapsedClassicTime');
  const { getItem: getStoredClassicStreak, setItem: setStoredClassicStreak } = useLocalStorage('classicStreak');
  const { getItem: getStoredClassicMaxStreak, setItem: setStoredClassicMaxStreak } = useLocalStorage('classicMaxStreak');
  const { getItem: getStoredClassicWins, setItem: setStoredClassicWins } = useLocalStorage('classicWins');
  const { getItem: getStoredClassicHintsUsed, setItem: setStoredClassicHintsUsed } = useLocalStorage('classicHintsUsed');
  const { getItem: getStoredClassicLastHintUsed, setItem: setStoredClassicLastHintUsed } = useLocalStorage('classicLastHintUsed');
  const { getItem: getStoredClassicMovesPlayed, setItem: setStoredClassicMovesPlayed } = useLocalStorage('classicMovesPlayed');
  
  const { getItem: getStoredLatestCasualDate, setItem: setStoredLatestCasualDate } = useLocalStorage('latestCasualDate');
  const { getItem: getStoredCasualTileOrder, setItem: setStoredCasualTileOrder} = useLocalStorage('casualTileOrder');
  const { getItem: getStoredCasualCompleted, setItem: setStoredCasualCompleted } = useLocalStorage('casualCompleted');
  const { getItem: getStoredCasualElapsedTime, setItem: setStoredCasualElapsedTime } = useLocalStorage('elapsedCasualTime');
  const { getItem: getStoredCasualStreak, setItem: setStoredCasualStreak } = useLocalStorage('casualStreak');
  const { getItem: getStoredCasualMaxStreak, setItem: setStoredCasualMaxStreak } = useLocalStorage('casualMaxStreak');
  const { getItem: getStoredCasualWins, setItem: setStoredCasualWins } = useLocalStorage('casualWins');
  const { getItem: getStoredCasualHintsUsed, setItem: setStoredCasualHintsUsed } = useLocalStorage('casualHintsUsed');
  const { getItem: getStoredCasualLastHintUsed, setItem: setStoredCasualLastHintUsed } = useLocalStorage('casualLastHintUsed');
  const { getItem: getStoredCasualMovesPlayed, setItem: setStoredCasualMovesPlayed } = useLocalStorage('casualMovesPlayed');

  const { getItem: getStoredHasSeenTutorial, setItem: setStoredHasSeenTutorial } = useLocalStorage('hasSeenTutorial');
  const { getItem: getStoredShowMatchingEdges, setItem: setStoredShowMatchingEdges } = useLocalStorage('showMatchingEdges');
  const [showMatchingEdges, setShowMatchingEdges] = useState<boolean>(
    () => getStoredShowMatchingEdges() ?? true
  );

  const greetingIcon = useMemo(() => {
    const svg = generateTileSVG("BBWBWWBW", 100);
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, []);
  const { image: howToImg } = useImage("how-to.png")
  const { image: helpIcon } = useImage("help.png")
  const { image: backIcon } = useImage("back.png")
  const { image: igIcon } = useImage("instagram.png")
  const { image: statsIcon } = useImage("stats.png")

  const { error: gameError, loading: gameLoading } = useQuery(GET_GAME_OF_THE_DAY, {
    variables: {
      date: getTodaysDate()
    },
    onCompleted: (gameData) => {
      setGameExists(gameData.gameOfTheDay.success)
      if (gameData.gameOfTheDay.success) {
        setActiveGameDate(gameData.gameOfTheDay.date);
        setGameData(gameData.gameOfTheDay);
      }
    }
  });

  const [startUserGame] = useMutation(START_USER_GAME);
  const [recordUserGameHint] = useMutation(RECORD_USER_GAME_HINT);
  const applyTimePercentileResult = useCallback((result?: { showRank?: boolean; timePercentile?: number | null }) => {
    const percentile = result?.timePercentile;
    if (result?.showRank === true && Number.isFinite(percentile)) {
      setTimePercentile(percentile as number);
      setShowTimePercentile(true);
    } else {
      setTimePercentile(null);
      setShowTimePercentile(false);
    }
  }, []);

  const [fetchTimePercentile] = useLazyQuery(GET_USER_GAME_PERCENTILE, {
    fetchPolicy: 'network-only',
  });

  const fetchTimePercentileRef = useRef(fetchTimePercentile);
  fetchTimePercentileRef.current = fetchTimePercentile;
  const applyTimePercentileResultRef = useRef(applyTimePercentileResult);
  applyTimePercentileResultRef.current = applyTimePercentileResult;

  const hintLength = 15;

  type StorageFunctions = {
    getStoredCompleted: () => boolean;
    setStoredElapsedTime: (value: number) => void;
    setStoredMovesPlayed: (value: number) => void;
    setStoredTileOrder: (value: string[]) => void;
    setStoredHintsUsed: (value: number) => void;
    setStoredLastHintUsed: (value: number) => void;
    getStoredElapsedTime: () => number;
    getStoredStreak: () => number;
    getStoredMaxStreak: () => number;
    getStoredWins: () => number;
    getStoredLatestDate: () => string;
    setStoredLatestDate: (value: string) => void;
    setStoredCompleted: (value: boolean) => void;
    getStoredTileOrder: () => string[];
    getStoredHintsUsed: () => number;
    getStoredLastHintUsed: () => number;
    getStoredMovesPlayed: () => number;
    setStoredStreak: (value: number) => void;
    setStoredMaxStreak: (value: number) => void;
    setStoredWins: (value: number) => void;
  };

  function getModeStorageFunctions(mode: string): StorageFunctions {
    const isClassic = mode === "classic";
    return {
      getStoredCompleted: isClassic ? getStoredClassicCompleted : getStoredCasualCompleted,
      setStoredElapsedTime: isClassic ? setStoredClassicElapsedTime : setStoredCasualElapsedTime,
      setStoredMovesPlayed: isClassic ? setStoredClassicMovesPlayed : setStoredCasualMovesPlayed,
      setStoredTileOrder: isClassic ? setStoredClassicTileOrder : setStoredCasualTileOrder,
      setStoredHintsUsed: isClassic ? setStoredClassicHintsUsed : setStoredCasualHintsUsed,
      setStoredLastHintUsed: isClassic ? setStoredClassicLastHintUsed : setStoredCasualLastHintUsed,
      getStoredElapsedTime: isClassic ? getStoredClassicElapsedTime : getStoredCasualElapsedTime,
      getStoredStreak: isClassic ? getStoredClassicStreak : getStoredCasualStreak,
      getStoredMaxStreak: isClassic ? getStoredClassicMaxStreak : getStoredCasualMaxStreak,
      getStoredWins: isClassic ? getStoredClassicWins : getStoredCasualWins,
      getStoredLatestDate: isClassic ? getStoredLatestClassicDate : getStoredLatestCasualDate,
      setStoredLatestDate: isClassic ? setStoredLatestClassicDate : setStoredLatestCasualDate,
      setStoredCompleted: isClassic ? setStoredClassicCompleted : setStoredCasualCompleted,
      getStoredTileOrder: isClassic ? getStoredClassicTileOrder : getStoredCasualTileOrder,
      getStoredHintsUsed: isClassic ? getStoredClassicHintsUsed : getStoredCasualHintsUsed,
      getStoredLastHintUsed: isClassic ? getStoredClassicLastHintUsed : getStoredCasualLastHintUsed,
      getStoredMovesPlayed: isClassic ? getStoredClassicMovesPlayed : getStoredCasualMovesPlayed,
      setStoredStreak: isClassic ? setStoredClassicStreak : setStoredCasualStreak,
      setStoredMaxStreak: isClassic ? setStoredClassicMaxStreak : setStoredCasualMaxStreak,
      setStoredWins: isClassic ? setStoredClassicWins : setStoredCasualWins
    };
  }

  function mulberry32(seed: number): () => number {
    let t = seed;
    return function () {
      t += 0x6D2B79F5;
      let r = Math.imul(t ^ (t >>> 15), t | 1);
      r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }
  
  function shuffleOrder<T>(array: T[], rng: () => number): T[] {
    const a = [...array];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function initializeNewGame(mode: string, persistToStorage = true) {
    const storage = getModeStorageFunctions(mode);
    const tiles = mode === "classic" ? gameData.classicTiles : gameData.casualTiles;

    if (persistToStorage) {
      storage.setStoredLatestDate(gameData.date);
      storage.setStoredCompleted(false);
    }
    const seed = gameData.date.charCodeAt(gameData.date.length - 1);
    const rng = mulberry32(seed);
    const shuffledTiles = shuffleOrder(tiles, rng);
    setGameTiles(shuffledTiles);
    if (persistToStorage) {
      storage.setStoredTileOrder(shuffledTiles);
    }
    setElapsedTime(0);
    if (persistToStorage) {
      storage.setStoredElapsedTime(0);
    }
    setHintsUsed(0);
    if (persistToStorage) {
      storage.setStoredHintsUsed(0);
    }
    setLastHintUsed(0);
    if (persistToStorage) {
      storage.setStoredLastHintUsed(0);
    }
    setMovesPlayed(0);
    if (persistToStorage) {
      storage.setStoredMovesPlayed(0);
    }
    if (persistToStorage) {
      trackEvent(`${mode} game started`, { userId: getUserId(getStoredUserId, setStoredUserId) });
    }
  }

  function persistCurrentGameProgress(mode: string) {
    const storage = getModeStorageFunctions(mode);
    storage.setStoredLatestDate(gameData.date);
    storage.setStoredCompleted(false);
    storage.setStoredTileOrder(gameTiles);
    storage.setStoredElapsedTime(elapsedTime);
    storage.setStoredHintsUsed(hintsUsed);
    storage.setStoredLastHintUsed(lastHintUsed);
    storage.setStoredMovesPlayed(movesPlayed);
    trackEvent(`${mode} game started`, { userId: getUserId(getStoredUserId, setStoredUserId) });
  }

  function resumeGame(mode: string) {
    const storage = getModeStorageFunctions(mode);
    setGameTiles(storage.getStoredTileOrder());
    setElapsedTime(storage.getStoredElapsedTime());
    setHintsUsed(storage.getStoredHintsUsed());
    setLastHintUsed(storage.getStoredLastHintUsed());
    setMovesPlayed(storage.getStoredMovesPlayed());
  }

  function needsFirstVisitTutorial(): boolean {
    return getStoredHasSeenTutorial() !== true;
  }

  function play(mode: string) {
    setMode(mode);
    setTimePercentile(null);
    setShowTimePercentile(false);
    setVictoryStats(null);
    const storage = getModeStorageFunctions(mode);
    const forceTutorial = needsFirstVisitTutorial();
    const tiles = mode === "classic" ? gameData.classicTiles : gameData.casualTiles;
    const numSolutions = mode === "classic" ? gameData.classicNumSolutions : gameData.casualNumSolutions;

    setSolutionTiles(tiles);
    setNumSolutions(numSolutions);

    startUserGame({
      variables: {
        userId: getUserId(getStoredUserId, setStoredUserId),
        date: gameData.date,
        mode: mode
      }
    });

    if (storage.getStoredLatestDate() !== gameData.date) {
      initializeNewGame(mode, !forceTutorial);
    } else {
      resumeGame(mode);
    }

    if (storage.getStoredLatestDate() === gameData.date && storage.getStoredCompleted()) {
      setModalState('victory');
    } else if (forceTutorial) {
      setForcedFirstHelp(true);
      setModalState('help');
    } else {
      setModalState('game');
    }
  }

  const [submitUserGameState] = useMutation(SUBMIT_USER_GAME, {
    onCompleted: (submitData) => {
      if (!submitData.submitUserGame.success) {
        trackEvent('submission failed', {
          tiles: gameTiles,
          userId: getUserId(getStoredUserId, setStoredUserId),
          date: getTodaysDate()});
        return;
      }
      if (submitData.submitUserGame.solved) {
        const storage = getModeStorageFunctions(mode);
        storage.setStoredStreak(submitData.submitUserGame.streak);
        storage.setStoredMaxStreak(submitData.submitUserGame.maxStreak);
        storage.setStoredWins(submitData.submitUserGame.wins);
        storage.setStoredCompleted(true);
        setVictoryStats({
          streak: submitData.submitUserGame.streak ?? 1,
          maxStreak: submitData.submitUserGame.maxStreak ?? 1,
          wins: submitData.submitUserGame.wins ?? 1,
        });
        applyTimePercentileResult({
          showRank: submitData.submitUserGame.showRank,
          timePercentile: submitData.submitUserGame.timePercentile,
        });
        offerClaimRef.current = submitData.submitUserGame.offerClaim && process.env.REACT_APP_PUZZLI_CLAIMS === "true"
        trackEvent('victory', {
          userId: getUserId(getStoredUserId, setStoredUserId),
          movesPlayed: movesPlayed,
          elapsedTimeMs: elapsedTime, 
          elapsedTime: formatTime(elapsedTime), 
          date: getTodaysDate()});
      }
    }
  });

  useEffect(() => {
    if (modalState !== 'victory' || !mode) return;
    const date = activeGameDate || gameData?.date;
    if (!date) return;
    if (!getModeStorageFunctions(mode).getStoredCompleted()) return;

    let cancelled = false;
    fetchTimePercentileRef.current({
      variables: {
        userId: getUserId(getStoredUserId, setStoredUserId),
        date,
        mode
      }
    }).then(({ data }) => {
      if (!cancelled && data?.userGamePercentile?.success) {
        applyTimePercentileResultRef.current(data.userGamePercentile);
      }
    }).catch(() => {
      // Keep percentile from submitUserGame if the follow-up query fails
    });

    return () => {
      cancelled = true;
    };
  }, [modalState, mode, activeGameDate, gameData?.date]);

  useEffect(() => {
    let interval = setInterval(() => {
      if (modalState === 'game' && !getModeStorageFunctions(mode).getStoredCompleted()) {
        setElapsedTime((elapsedTime) => elapsedTime + 100);
      }
    }, 100);
    return () => {
      clearInterval(interval)
    }
  }, [modalState, mode]);

  useEffect(() => {
    if (!gameLoading && elapsedTime >= 1000) {
      getModeStorageFunctions(mode).setStoredElapsedTime(elapsedTime);
    }
  }, [elapsedTime, gameLoading, mode]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (hintsUsed >= 1 && (gameTiles[4] === event.over.id || event.over === null) ||
        hintsUsed >= 2 && (gameTiles[0] === event.over.id || event.over === null) ||
        hintsUsed >= 3 && (gameTiles[8] === event.over.id || event.over === null)) {
      setActiveId(null);
      return;
    }
    const { active, over } = event;
    setMovesPlayed(prev => {
      const newMoves = prev + 1;
      getModeStorageFunctions(mode).setStoredMovesPlayed(newMoves);
      if (newMoves === 20) {
        trackEvent('20_moves');
      }
      if (newMoves === 50) {
        trackEvent('50_moves');
      }
      return newMoves;
    });

    if (over !== null && active.id !== over?.id) {
      let nextTiles: string[] = null;
      setGameTiles((gameTiles) => {
        const oldIndex = gameTiles.findIndex((tile) => tile === active.id);
        const newIndex = gameTiles.findIndex((tile) => tile === over.id);

        nextTiles = arraySwap(gameTiles, oldIndex, newIndex);
        if (!getModeStorageFunctions(mode).getStoredCompleted()) {
          getModeStorageFunctions(mode).setStoredTileOrder(nextTiles);
          if (solutionValid(nextTiles)) {
            submitGame(nextTiles);
          }
        }
        return nextTiles;
      });
    }
    setActiveId(null);
  }, [hintsUsed, mode]);

  function hint() {
    let nextTiles: string[] = null;
    const hintNumber = hintsUsed + 1;
    getModeStorageFunctions(mode).setStoredHintsUsed(hintNumber);
    setHintsUsed(hintNumber);
    setLastHintUsed(elapsedTime);
    getModeStorageFunctions(mode).setStoredLastHintUsed(elapsedTime);
    recordUserGameHint({
      variables: {
        userId: getUserId(getStoredUserId, setStoredUserId),
        date: activeGameDate || getTodaysDate(),
        mode: mode,
        hintNumber: hintNumber,
        elapsedTime: elapsedTime
      }
    });
    trackEvent('hint', {
      hints: hintsUsed,
      userId: getUserId(getStoredUserId, setStoredUserId), 
      elapsedTimeMs: elapsedTime, 
      elapsedTime: formatTime(elapsedTime), 
      date: getTodaysDate()});
    setGameTiles((gameTiles) => {
      const indexToFind = hintsUsed === 0 ? 4 : hintsUsed === 1 ? 0 : 8;
      const solutionTile = solutionTiles[indexToFind];
      const oldIndex = gameTiles.findIndex(tile => tile === solutionTile)!;
      const newIndex = indexToFind; 

      nextTiles = arraySwap(gameTiles, oldIndex, newIndex);

      getModeStorageFunctions(mode).setStoredTileOrder(nextTiles);
      if (solutionValid(nextTiles)) {
        submitGame(nextTiles);
      }
      return nextTiles;
    });
  }

  function shuffle() {
    let nextTiles: string[] = null;
    setGameTiles((gameTiles) => {
      nextTiles = [...gameTiles];
      let currentIndex = nextTiles.length, temporaryValue, randomIndex;

      while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
    
        temporaryValue = nextTiles[currentIndex];
        nextTiles[currentIndex] = nextTiles[randomIndex];
        nextTiles[randomIndex] = temporaryValue;
      }

      if (hintsUsed > 2) {
        const oldIndex = nextTiles.findIndex(tile => tile === solutionTiles[8])!;
        const newIndex = 8;
        nextTiles = arraySwap(nextTiles, oldIndex, newIndex);
      }
      if (hintsUsed > 1) {
        const oldIndex = nextTiles.findIndex(tile => tile === solutionTiles[0])!;
        const newIndex = 0;
        nextTiles = arraySwap(nextTiles, oldIndex, newIndex);
      }
      if (hintsUsed > 0) {
        const oldIndex = nextTiles.findIndex(tile => tile === solutionTiles[4])!;
        const newIndex = 4;
        nextTiles = arraySwap(nextTiles, oldIndex, newIndex);
      }
      
      if (!getModeStorageFunctions(mode).getStoredCompleted()) {
        getModeStorageFunctions(mode).setStoredTileOrder(nextTiles);
      }
    
      return nextTiles;
    });
  }

  const triggerVictory = () => {
    confetti({
      particleCount: 500,
      spread: 360,
      origin: { x: 0.5, y: 0.5 }
    });
    const tiles = document.querySelectorAll('.tile');
    tiles.forEach((tile) => {
      setTimeout(() => {
        tile.classList.add('wave');
        setTimeout(() => {
          tile.classList.remove('wave');
        }, 1250);
      }, 0);
    });

    setTimeout(() => {
      if (offerClaimRef.current) {
        confetti({
          particleCount: 500,
          spread: 360,
          origin: { x: 0.5, y: 0.5 }
        });
        setModalState('offer');
      } else {
        setModalState('victory'); 
      }
    }, 1500)
  };

  const submitGame = (tiles: String[]) => {
    triggerVictory();
    submitUserGameState({
      variables: {
        userId: getUserId(getStoredUserId, setStoredUserId),
        date: activeGameDate || getTodaysDate(),
        elapsedTime: getModeStorageFunctions(mode).getStoredElapsedTime(),
        tiles: tiles,
        mode: mode
      }
    })
  }

  if (!gameExists || gameError || process.env.REACT_APP_PUZZLI_MAINTENANCE === "true") return <ErrorModal greetingIcon={greetingIcon}/>;

  const dragBlocked = modalState === 'help' && forcedFirstHelp;
  const tileDisplayWidth = getTileDisplayWidth();

  const tiles = gameTiles.map((tile) => {
    const hintLocked =
      (hintsUsed >= 1 && solutionTiles[4] === tile) ||
      (hintsUsed >= 2 && solutionTiles[0] === tile) ||
      (hintsUsed === 3 && solutionTiles[8] === tile);

    return (
    <SortableItem
      id={tile}
      key={tile}
      pattern={tile}
      activeId={activeId}
      hintLocked={hintLocked}
      disabled={dragBlocked || hintLocked}
    />
    );
  });

  const hintDisabled = hintsUsed >= 3 || getModeStorageFunctions(mode).getStoredCompleted() || elapsedTime - lastHintUsed < hintLength * 1000 || dragBlocked;

  const modeStorage = mode ? getModeStorageFunctions(mode) : null;
  const storedStreak = modeStorage?.getStoredStreak() ?? 0;
  const storedMaxStreak = modeStorage?.getStoredMaxStreak() ?? 0;
  const storedWins = modeStorage?.getStoredWins() ?? 0;
  const streak = victoryStats?.streak ?? (modeStorage?.getStoredCompleted() ? storedStreak : storedStreak + 1);
  const maxStreak = victoryStats?.maxStreak ?? Math.max(storedMaxStreak, streak);
  const wins = victoryStats?.wins ?? (modeStorage?.getStoredCompleted()
    ? (storedWins || storedMaxStreak || storedStreak || 1)
    : (storedWins || storedMaxStreak || storedStreak || 0) + 1);

  return (
    <div className="app-viewport">
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <GreetingModal 
          onGreetingClose={(mode: string) => {
            play(mode);
          }}
          showModal={modalState === 'greeting'}
          gameNumber={getGameNumber()} 
          greetingIcon={greetingIcon}
          disableButton={gameLoading || !gameTiles}
          onAdminShortcut={goToAdminViaShortcut}>
        </GreetingModal>
        <HelpModal 
          onHelpClose={() => {
            if (forcedFirstHelp) {
              setStoredHasSeenTutorial(true);
              setForcedFirstHelp(false);
              if (mode) {
                persistCurrentGameProgress(mode);
              }
            }
            setModalState('game');
          }}
          showModal={modalState === 'help'}
          howToImg={howToImg}
          mode={mode}
          showMatchingEdges={showMatchingEdges}
          onToggleMatchingEdges={(value) => {
            setShowMatchingEdges(value);
            setStoredShowMatchingEdges(value);
          }}>
        </HelpModal>
        <VictoryModal
          onVictoryClose={() => setModalState('game')}
          showModal={modalState === 'victory'}
          elapsedTime={formatTime(elapsedTime)}
          gameNumber={getGameNumber()} 
          streak={streak}
          maxStreak={maxStreak}
          userId={getUserId(getStoredUserId, setStoredUserId)}
          wins={wins}
          hintsUsed={hintsUsed}
          gameTiles={gameTiles}
          mode={mode}
          showTimePercentile={showTimePercentile}
          timePercentile={timePercentile}
          onSwitchMode={() => {
            setModalState('game');
            play(mode === 'classic' ? 'casual' : 'classic');
          }}
        >
        </VictoryModal>
        <OfferModal
          onOfferClose={() => setModalState('game')}
          showModal={modalState === 'offer'}
          maxStreak={maxStreak}
          userId={getUserId(getStoredUserId, setStoredUserId)}>
        </OfferModal>
        {
        modalState !== 'greeting' &&
          <div>
            <div 
              style={{
                fontSize: window.innerWidth > 500 ? 100 : 80, 
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
              onClick={() => setModalState('greeting')}
            > 
              Puzzli 
            </div>
            <NavigationBar 
                onBackOpen={() => {
                  if (!forcedFirstHelp) {
                    setModalState('greeting');
                  }
                }}
                onHelpOpen={() => {
                  setForcedFirstHelp(false);
                  setModalState('help');
                }}
                onStatsOpen={() => setModalState('victory')}
                completed={(getModeStorageFunctions(mode).getStoredCompleted())}
                elapsedTime={formatTime(elapsedTime)} helpIcon={helpIcon} igIcon={igIcon} statsIcon={statsIcon} backIcon={backIcon}/>
            {
              gameExists ?
              <div style={{ pointerEvents: dragBlocked ? 'none' : 'auto' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={gameTiles.map(tile => tile)} strategy={rectSwappingStrategy}>
                  <Grid
                    columns={3}
                    tileSize={tileDisplayWidth}
                    gameTiles={gameTiles}
                    showMatchingEdges={showMatchingEdges}
                  >
                    {tiles}
                  </Grid>
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <Item
                      pattern={activeId}
                      isDragging
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
              </div>
              :
              <div style={{fontSize: 80}}> Error loading game. Check back soon! </div>
            }
            <div className="game-footer">
              <div className="game-solutions"> 
                Total Solutions: <span style={{ color: getSolutionsFontColor(numSolutions) }}>{numSolutions}</span> 
              </div>
              <div className="game-action-bar">
                <Button 
                  onClick={hint} 
                  className={hintDisabled ? 'game-button-disabled' : 'game-button'}
                  disabled={hintDisabled}>
                  Hint{getModeStorageFunctions(mode).getStoredCompleted() || hintsUsed >= 3 || elapsedTime - lastHintUsed >= hintLength * 1000 ? "" : " (" + (hintLength - Math.floor((elapsedTime - lastHintUsed)/1000)) + ")"}
                </Button>
                <Button onClick={shuffle} className={getModeStorageFunctions(mode).getStoredCompleted() || dragBlocked ? 'game-button-disabled' : 'game-button'} disabled={getModeStorageFunctions(mode).getStoredCompleted() || dragBlocked}>
                  Shuffle 
                </Button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
}